import { KatButton, KatStatusindicator } from "@amzn/katal-react";
import React from "react";
import { useTranslation } from "react-i18next";
import { type UploadProgress, UploadStatus } from "src/api/interfaces/nest";
import "./index.css";

interface NestUploadResultProps {
  uploads: UploadProgress[];
  onClose: () => void;
  onRetry: (fileName?: string) => void;
  onSuccessClick: (fileName: string) => void;
}

export function NestUploadResult({
  uploads,
  onClose,
  onRetry,
  onSuccessClick,
}: NestUploadResultProps) {
  const { t } = useTranslation();
  const successful = uploads.filter((u) => u.status === UploadStatus.DONE);
  const failed4xx = uploads.filter(
    (u) =>
      u.status === UploadStatus.ERROR &&
      u.statusCode &&
      u.statusCode >= 400 &&
      u.statusCode < 500
  );
  const failed5xx = uploads.filter(
    (u) =>
      u.status === UploadStatus.ERROR && u.statusCode && u.statusCode >= 500
  );
  const allSuccess = failed4xx.length === 0 && failed5xx.length === 0;

  return (
    <div
      className={`nest-upload-result ${allSuccess ? "nest-upload-result--success" : "nest-upload-result--error"}`}
    >
      <div className="nest-upload-result__header">
        <KatStatusindicator
          variant={allSuccess ? "success" : "error"}
          label={
            allSuccess
              ? t("nest_upload_complete")
              : t("nest_upload_complete_with_errors")
          }
        />
      </div>

      {failed4xx.length > 0 && (
        <div className="nest-upload-result__section">
          <h3 className="nest-upload-result__title--error">
            {t("nest_upload_failed", { count: failed4xx.length })}
          </h3>
          <ul className="nest-upload-result__list">
            {failed4xx.map((u) => (
              <li key={u.fileName} className="nest-upload-result__item">
                <KatStatusindicator variant="error" label={u.fileName} />
                {u.error && (
                  <span className="nest-upload-result__error-msg">
                    {t(u.error.message.key, u.error.message.parameters)}
                  </span>
                )}
              </li>
            ))}
          </ul>
        </div>
      )}

      {failed5xx.length > 0 && (
        <div className="nest-upload-result__section">
          <div className="nest-upload-result__section-header">
            <h3 className="nest-upload-result__title--warning">
              {t("nest_upload_server_error", { count: failed5xx.length })}
            </h3>
            {failed5xx.length > 1 && (
              <KatButton variant="secondary" onClick={() => onRetry()}>
                {t("nest_upload_retry")}
              </KatButton>
            )}
          </div>
          <ul className="nest-upload-result__list">
            {failed5xx.map((u) => (
              <li
                key={u.fileName}
                className="nest-upload-result__item nest-upload-result__item--row"
              >
                <KatStatusindicator variant="alert" label={u.fileName} />
                <KatButton
                  variant="secondary"
                  onClick={() => onRetry(u.fileName)}
                >
                  {t("nest_upload_retry")}
                </KatButton>
              </li>
            ))}
          </ul>
        </div>
      )}

      {successful.length > 0 && (
        <div className="nest-upload-result__section">
          <h3 className="nest-upload-result__title--success">
            {t("nest_upload_successful", { count: successful.length })}
          </h3>
          <ul className="nest-upload-result__list">
            {successful.map((u) => (
              <li
                key={u.fileName}
                className="nest-upload-result__item nest-upload-result__item--row"
              >
                <KatStatusindicator variant="success" label={u.fileName} />
                <KatButton
                  variant="secondary"
                  onClick={() => onSuccessClick(u.fileName)}
                >
                  {t("nest_upload_open_document")}
                </KatButton>
              </li>
            ))}
          </ul>
        </div>
      )}

      <KatButton onClick={onClose}>{t("nest_upload_close")}</KatButton>
    </div>
  );
}
